import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useContext,
  useImperativeHandle,
  useState,
} from 'react';
import classNames from 'classnames';
import { Button } from 'ncoded-component-library';
import { CollapsibleSection } from 'components/CollapsibleSection';
import useCallbackRef from 'hooks/useCallbackRef';
import { CollapsibleRef } from 'ncoded-component-library/build/components/molecules/Collapsible/Collapsible.component';
import { useTranslation } from 'react-i18next';
import confirm from 'modules/confirm';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import Tag from 'components/ListingPortfolioCard/components/Tag';
import { COLOR_VARIANT } from 'models/Colors';
import DataRoomStorage from '../DataRoomStorage';

import './ManageKeyFeatures.styles.scss';

type ManageKeyFeaturesProps = {
  className?: string;
};

const ManageKeyFeatures: ForwardRefRenderFunction<
  CollapsibleRef,
  ManageKeyFeaturesProps
> = (props, ref) => {
  const { className } = props;

  const [isLoading, setIsLoading] = useState(false);

  const [manageKeyFeatures, manageKeyFeaturesRef] =
    useCallbackRef<CollapsibleRef>(null);

  const {
    dataRoom: { _id },
    isEnterpriseHead,
    isDisabledDataRoom,
    isDisabledQA,
    setDataRoom,
  } = useContext(DataRoomContext);

  const { t } = useTranslation();

  const classes = classNames('manage-key-features', 'white-panel', className);
  const disabled = isLoading || isEnterpriseHead;

  useImperativeHandle(ref, () => manageKeyFeatures, [manageKeyFeatures]);

  const updateIsDisabledQA = useCallback(
    async (value: boolean) => {
      try {
        setIsLoading(true);
        await dataRoomApi.room.toggleQa(_id, value);
        setDataRoom((prev) => ({ ...prev, isDisabledQA: value }));
      } catch (error) {
        return;
      } finally {
        setIsLoading(false);
      }
    },
    [_id, setDataRoom],
  );

  const toggleQA = useCallback(async () => {
    if (isEnterpriseHead) return;

    if (!isDisabledQA) {
      if (
        await confirm(
          {
            title: t('confirmDisablingQA'),
            content: t('preventBuySideMembersFromAskingQuestions'),
            type: 'delete',
            submitBtnText: t('disableQ&A'),
            cancelBtnText: t('noCancel'),
          },
          true,
        )
      ) {
        updateIsDisabledQA(true);
      }
    } else {
      updateIsDisabledQA(false);
    }
  }, [isDisabledQA, isEnterpriseHead, t, updateIsDisabledQA]);

  const updateIsDisabled = useCallback(
    async (value: boolean) => {
      try {
        setIsLoading(true);
        await dataRoomApi.room.disableDataRoom(_id, value);
        setDataRoom((prev) => ({ ...prev, isDisabled: value }));
      } catch (error) {
        return;
      } finally {
        setIsLoading(false);
      }
    },
    [_id, setDataRoom],
  );

  const toggleDisableDR = useCallback(async () => {
    if (isEnterpriseHead) return;

    if (!isDisabledDataRoom) {
      if (
        await confirm(
          {
            title: t('confirmPausingDR'),
            content: t('pauseDataRoomDescription'),
            type: 'delete',
            submitBtnText: t('pauseDataRoom'),
            cancelBtnText: t('noCancel'),
          },
          true,
        )
      ) {
        updateIsDisabled(true);
      }
    } else {
      updateIsDisabled(false);
    }
  }, [isDisabledDataRoom, isEnterpriseHead, t, updateIsDisabled]);

  const closeDataRoom = useCallback(async () => {
    if (isEnterpriseHead) return;

    if (
      await confirm(
        {
          title: t('confirmYourClosingDeal'),
          content: t('endOfTheDealDescription'),
          type: 'delete',
          submitBtnText: t('closeDataRoom'),
          cancelBtnText: t('noCancel'),
          checkboxAgreeLabel: t('yesImSureCloseDR'),
        },
        true,
      )
    ) {
      try {
        setIsLoading(true);
        await dataRoomApi.room.closeDataRoom(_id, true);
        setDataRoom((prev) => ({ ...prev, isClosed: true }));
      } catch (e) {
        return;
      } finally {
        setIsLoading(false);
      }
    }
  }, [_id, isEnterpriseHead, setDataRoom, t]);

  return (
    <CollapsibleSection
      className={classes}
      ref={manageKeyFeaturesRef}
      trigger={
        <div className="trigger__content">
          <p className="name">{t('manageKeyFeatures')}</p>
          <p>{t('manageKeyFeaturesDescription')}</p>
        </div>
      }
    >
      <div className="info">
        <div>
          <p className="info__title">
            <span>{t('qAndA')}</span>
            <Tag
              variant={
                isDisabledQA ? COLOR_VARIANT.RED_FULL : COLOR_VARIANT.GREEN_FULL
              }
              content={t(isDisabledQA ? 'disabled' : 'active')}
            />
          </p>
          <p className="info__description">
            {t('preventBuySideMembersFromAskingQuestions')}
          </p>
        </div>
        <Button
          variant="outline"
          className={classNames({ 'warning-action-button': !isDisabledQA })}
          onClick={toggleQA}
          disabled={disabled}
        >
          {t(isDisabledQA ? 'enableQ&A' : 'disableQ&A')}
        </Button>
      </div>

      <div className="info">
        <div>
          <p className="info__title">
            <span>{t('dataRoom')}</span>
            <Tag
              variant={
                isDisabledDataRoom
                  ? COLOR_VARIANT.RED_FULL
                  : COLOR_VARIANT.GREEN_FULL
              }
              content={t(isDisabledDataRoom ? 'paused' : 'active')}
            />
          </p>
          <p className="info__description">{t('pauseDataRoomDescription')}</p>
        </div>
        <Button
          variant="outline"
          className={classNames({
            'warning-action-button': !isDisabledDataRoom,
          })}
          onClick={toggleDisableDR}
          disabled={disabled}
        >
          {t(isDisabledDataRoom ? 'unpauseDataRoom' : 'pauseDataRoom')}
        </Button>
      </div>

      <div className="info">
        <div>
          <p className="info__title">
            <span>{t('endOfTheDeal')}</span>
          </p>
          <p className="info__description">{t('endOfTheDealDescription')}</p>
        </div>
        <Button
          variant="outline"
          className="warning-action-button"
          onClick={closeDataRoom}
          disabled={disabled}
        >
          {t('closeDataRoom')}
        </Button>
      </div>

      <DataRoomStorage />
    </CollapsibleSection>
  );
};

export default forwardRef(ManageKeyFeatures);
